/* global document, window, localStorage, fetch, CustomEvent */

/**
 * /wishlist/ page — client-side hydration.
 *
 * Reads WC product IDs from localStorage.icfWishlistIds, fetches the
 * matching products via the WC Store API
 * (`/wp-json/wc/store/v1/products?include=…`), renders a grid of
 * cards, and wires the per-card × Remove button. Re-renders on any
 * `icf:wishlist-changed` event (dispatched by single-product.js + by
 * this module) or `storage` event (cross-tab sync).
 *
 * Shared with header-cart-wishlist.js via:
 *   - the localStorage key  `icfWishlistIds`
 *   - the custom event       `icf:wishlist-changed`  (detail.ids)
 *
 * No-op on any page where `[data-wishlist-page]` is absent.
 */
( function () {
	const WL_KEY = 'icfWishlistIds';

	/**
	 * Resolve the WP REST API root. setup.php's wp_enqueue_scripts hook
	 * inlines `window.icfRestUrl` from PHP's site_url() (= WordPress
	 * Address option), which is reliable on subdirectory installs even
	 * when home_url() is misconfigured. Fall back to '/wp-json' only as
	 * a last resort.
	 */
	const restRoot = (
		typeof window.icfRestUrl === 'string' && window.icfRestUrl ? window.icfRestUrl : '/wp-json'
	).replace( /\/$/, '' );
	const API_BASE = `${ restRoot }/wc/store/v1/products`;

	const onReady = fn => {
		if ( document.readyState !== 'loading' ) {
			fn();
		} else {
			document.addEventListener( 'DOMContentLoaded', fn );
		}
	};

	const loadIds = () => {
		try {
			const raw = localStorage.getItem( WL_KEY );
			const arr = raw ? JSON.parse( raw ) : [];
			return Array.isArray( arr )
				? arr.map( Number ).filter( n => Number.isFinite( n ) && n > 0 )
				: [];
		} catch ( err ) {
			return [];
		}
	};

	const saveIds = ids => {
		try {
			localStorage.setItem( WL_KEY, JSON.stringify( ids ) );
		} catch ( err ) {
			// storage unavailable / quota — fail silently
		}
		document.dispatchEvent( new CustomEvent( 'icf:wishlist-changed', { detail: { ids } } ) );
	};

	/**
	 * Returns:
	 *   - Array of products on success (possibly empty if none match).
	 *   - null  on a network / HTTP / parse failure. Callers treat null
	 *           as "couldn't determine the truth" and must NOT self-heal
	 *           localStorage from it — that would wipe a working wishlist
	 *           on a transient API failure.
	 */
	const fetchProducts = async ids => {
		if ( ! ids.length ) {
			return [];
		}
		const params = new URLSearchParams();
		params.set( 'include', ids.join( ',' ) );
		params.set( 'per_page', String( Math.max( ids.length, 1 ) ) );
		const url = `${ API_BASE }?${ params.toString() }`;

		try {
			const res = await fetch( url, {
				credentials: 'same-origin',
				headers: { Accept: 'application/json' },
			} );
			if ( ! res.ok ) {
				// eslint-disable-next-line no-console
				console.error( `[wishlist] Store API ${ res.status } from ${ url }` );
				return null;
			}
			const data = await res.json();
			if ( ! Array.isArray( data ) ) {
				// eslint-disable-next-line no-console
				console.error( '[wishlist] Store API returned non-array payload:', data );
				return null;
			}
			return data;
		} catch ( err ) {
			// eslint-disable-next-line no-console
			console.error( '[wishlist] Store API fetch failed:', err );
			return null;
		}
	};

	const escapeHtml = str =>
		String( str == null ? '' : str )
			.replace( /&/g, '&amp;' )
			.replace( /</g, '&lt;' )
			.replace( />/g, '&gt;' )
			.replace( /"/g, '&quot;' )
			.replace( /'/g, '&#039;' );

	const escapeAttr = escapeHtml;

	const formatPrice = product => {
		const p = product && product.prices;
		if ( ! p ) {
			return '';
		}
		const minor = parseInt( p.price, 10 );
		if ( ! Number.isFinite( minor ) ) {
			return '';
		}
		const minorUnit = Number.isFinite( parseInt( p.currency_minor_unit, 10 ) )
			? parseInt( p.currency_minor_unit, 10 )
			: 2;
		const value = minor / Math.pow( 10, minorUnit );
		const decimalSep = p.currency_decimal_separator || '.';
		const thousandSep = p.currency_thousand_separator || ',';
		const [ intPart, fracPart = '' ] = value.toFixed( minorUnit ).split( '.' );
		const intWithThousands = intPart.replace( /\B(?=(\d{3})+(?!\d))/g, thousandSep );
		const formatted =
			minorUnit > 0 ? `${ intWithThousands }${ decimalSep }${ fracPart }` : intWithThousands;
		return `${ p.currency_prefix || '' }${ formatted }${ p.currency_suffix || '' }`;
	};

	const formatTotal = products => {
		if ( ! products.length ) {
			return '';
		}
		const first = products[ 0 ].prices || {};
		const minorUnit = Number.isFinite( parseInt( first.currency_minor_unit, 10 ) )
			? parseInt( first.currency_minor_unit, 10 )
			: 2;
		const sumMinor = products.reduce( ( acc, p ) => {
			const v = parseInt( p && p.prices && p.prices.price, 10 );
			return acc + ( Number.isFinite( v ) ? v : 0 );
		}, 0 );
		const value = sumMinor / Math.pow( 10, minorUnit );
		const decimalSep = first.currency_decimal_separator || '.';
		const thousandSep = first.currency_thousand_separator || ',';
		const [ intPart, fracPart = '' ] = value.toFixed( minorUnit ).split( '.' );
		const intWithThousands = intPart.replace( /\B(?=(\d{3})+(?!\d))/g, thousandSep );
		const formatted =
			minorUnit > 0 ? `${ intWithThousands }${ decimalSep }${ fracPart }` : intWithThousands;
		return `${ first.currency_prefix || '' }${ formatted }${ first.currency_suffix || '' }`;
	};

	const categoryLine = product => {
		const cats = Array.isArray( product.categories ) ? product.categories : [];
		return cats
			.slice( 0, 2 )
			.map( c => c && c.name )
			.filter( Boolean )
			.join( ' · ' );
	};

	const renderCard = product => {
		const image = ( product.images && product.images[ 0 ] ) || {};
		const imageUrl = image.thumbnail || image.src || '';
		const permalink = product.permalink || '#';
		const inStock = product.is_in_stock !== false;
		const ctaLabel = inStock ? 'Add to Cart' : 'Out of stock';

		// In-stock: CTA is a real button that triggers AJAX add-to-cart
		// in this script (handleAddToCartClick). Out-of-stock: degrade to
		// a disabled link to the product page so users can still get to
		// the PDP for back-in-stock info etc.
		const cta = inStock
			? `<button type="button" class="btn btn--primary text-light" data-add-to-cart data-product-id="${ escapeAttr( product.id ) }" data-product-name="${ escapeAttr( product.name ) }">${ escapeHtml( ctaLabel ) }</button>`
			: `<a href="${ escapeAttr( permalink ) }" class="btn btn--primary text-light" aria-disabled="true">${ escapeHtml( ctaLabel ) }</a>`;

		return `
			<div class="wl-card" data-product-id="${ escapeAttr( product.id ) }" data-product-permalink="${ escapeAttr( permalink ) }">
				<div class="wl-card__media" style="background-image:url('${ escapeAttr( imageUrl ) }');"></div>
				<button class="wl-card__remove" type="button" aria-label="Remove from wishlist" data-wishlist-remove>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6L18 18M6 18L18 6"/></svg>
				</button>
				<div class="wl-card__body">
					<span class="wl-card__category lh-sm">${ escapeHtml( categoryLine( product ) ) }</span>
					<h3 class="wl-card__name"><a href="${ escapeAttr( permalink ) }">${ escapeHtml(
						product.name
					) }</a></h3>
					<span class="wl-card__price lh-sm">${ formatPrice( product ) }</span>
					<div class="wl-card__cta">
						${ cta }
					</div>
				</div>
			</div>
		`;
	};

	const renderGrid = ( root, products ) => {
		const grid = root.querySelector( '[data-wishlist-grid]' );
		const toolbar = root.querySelector( '[data-wishlist-toolbar]' );
		const empty = root.querySelector( '[data-wishlist-empty]' );
		const countEl = root.querySelector( '[data-wishlist-count]' );
		const totalEl = root.querySelector( '[data-wishlist-total]' );
		const subEl = root.querySelector( '[data-wishlist-subtitle]' );

		if ( ! grid ) {
			return;
		}

		// Preserve order from the saved id list so the UI matches what the
		// user expects after toggling.
		const savedOrder = loadIds();
		const byId = new Map( products.map( p => [ p.id, p ] ) );
		const ordered = savedOrder.map( id => byId.get( id ) ).filter( Boolean );

		grid.innerHTML = ordered.map( renderCard ).join( '' );

		if ( toolbar ) {
			toolbar.hidden = false;
		}
		if ( empty ) {
			empty.hidden = true;
		}
		if ( countEl ) {
			countEl.textContent = String( ordered.length );
		}
		if ( totalEl ) {
			totalEl.textContent = formatTotal( ordered );
		}
		if ( subEl ) {
			const n = ordered.length;
			subEl.textContent =
				n === 1
					? '1 saved item. Move it to your cart whenever you’re ready.'
					: `${ n } saved items. Move them to your cart whenever you’re ready.`;
		}
	};

	const showEmpty = ( root, hadError ) => {
		const grid = root.querySelector( '[data-wishlist-grid]' );
		const toolbar = root.querySelector( '[data-wishlist-toolbar]' );
		const empty = root.querySelector( '[data-wishlist-empty]' );
		const subEl = root.querySelector( '[data-wishlist-subtitle]' );

		if ( grid ) {
			grid.innerHTML = '';
		}
		if ( toolbar ) {
			toolbar.hidden = true;
		}
		if ( empty ) {
			empty.hidden = false;
		}
		if ( subEl ) {
			subEl.textContent = hadError
				? 'We couldn’t load your saved items right now. Please try again.'
				: 'No saved items yet.';
		}
	};

	const handleRemoveClick = ( root, e ) => {
		const btn = e.target.closest( '[data-wishlist-remove]' );
		if ( ! btn ) {
			return;
		}
		const card = btn.closest( '.wl-card[data-product-id]' );
		if ( ! card ) {
			return;
		}
		const id = Number( card.getAttribute( 'data-product-id' ) );
		if ( ! id ) {
			return;
		}

		// Animate out, then save (which dispatches icf:wishlist-changed,
		// which triggers a re-render of this page + header badge update).
		card.style.opacity = '0';
		card.style.transform = 'scale(0.94)';
		window.setTimeout( () => {
			const ids = loadIds().filter( other => other !== id );
			saveIds( ids );
		}, 220 );
	};

	/**
	 * Apply the `fragments` map returned by WC's AJAX add_to_cart endpoint.
	 * Same behaviour as WC core's add-to-cart.js — swap each matching node
	 * with the server-rendered replacement. Our header cart-count fragment
	 * is registered in app/wc-template-hooks.php.
	 */
	const applyFragments = fragments => {
		if ( ! fragments || typeof fragments !== 'object' ) {
			return;
		}
		Object.keys( fragments ).forEach( selector => {
			document.querySelectorAll( selector ).forEach( el => {
				const tmp = document.createElement( 'template' );
				tmp.innerHTML = String( fragments[ selector ] ).trim();
				const replacement = tmp.content.firstElementChild;
				if ( replacement && el.parentNode ) {
					el.parentNode.replaceChild( replacement, el );
				}
			} );
		} );
	};

	/**
	 * Render the same success notice the PDP shows on a server-side add:
	 *   <div class="woocommerce-message" role="alert">
	 *     <a href="…" tabindex="1" class="button wc-forward">View cart</a>
	 *     “Product Name” has been added to your cart.
	 *   </div>
	 * Inserted into `[data-wishlist-notices]` which carries the standard
	 * `.woocommerce-notices-wrapper` class, so existing _woocommerce.scss
	 * styling applies unchanged.
	 */
	const showAddedNotice = ( root, productName ) => {
		const wrapper = root.querySelector( '[data-wishlist-notices]' );
		if ( ! wrapper ) {
			return;
		}
		const cartUrl = root.getAttribute( 'data-cart-url' ) || '/cart/';
		const message = document.createElement( 'div' );
		message.className = 'woocommerce-message';
		message.setAttribute( 'role', 'alert' );
		message.innerHTML =
			`<a href="${ escapeAttr( cartUrl ) }" tabindex="1" class="button wc-forward">${ escapeHtml(
				'View cart'
			) }</a>` +
			`“${ escapeHtml( productName ) }” has been added to your cart.`;

		wrapper.innerHTML = '';
		wrapper.appendChild( message );

		try {
			wrapper.scrollIntoView( { behavior: 'smooth', block: 'start' } );
		} catch ( err ) {
			// older browsers — ignore
		}
	};

	const handleAddToCartClick = async ( root, e ) => {
		const btn = e.target.closest( '[data-add-to-cart]' );
		if ( ! btn ) {
			return;
		}
		e.preventDefault();
		e.stopPropagation();

		const productId = Number( btn.getAttribute( 'data-product-id' ) );
		if ( ! productId ) {
			return;
		}
		const productName = btn.getAttribute( 'data-product-name' ) || '';
		const endpoint = root.getAttribute( 'data-add-to-cart-url' );
		if ( ! endpoint ) {
			return;
		}

		const originalLabel = btn.textContent;
		btn.disabled = true;
		btn.classList.add( 'is-loading' );

		try {
			const body = new URLSearchParams();
			body.set( 'product_id', String( productId ) );
			body.set( 'quantity', '1' );

			const res = await fetch( endpoint, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
					Accept: 'application/json',
				},
				body: body.toString(),
			} );

			if ( ! res.ok ) {
				throw new Error( `HTTP ${ res.status }` );
			}

			const data = await res.json();

			// WC signals an unaddable product (sold out, validation fail) with
			// `error: true` plus a `product_url` to bounce to. Mirror that —
			// the user gets routed to the PDP where the real error renders.
			if ( data && data.error && data.product_url ) {
				window.location.href = data.product_url;
				return;
			}

			applyFragments( data && data.fragments );
			showAddedNotice( root, productName );
		} catch ( err ) {
			// eslint-disable-next-line no-console
			console.error( '[wishlist] AJAX add-to-cart failed:', err );
			btn.textContent = originalLabel;
			btn.disabled = false;
			btn.classList.remove( 'is-loading' );
			return;
		}

		btn.textContent = originalLabel;
		btn.disabled = false;
		btn.classList.remove( 'is-loading' );
	};

	/**
	 * Clicking anywhere on the card (outside the remove button, the CTA,
	 * or the product-name link which already navigates) takes the user
	 * to the product page. Keeps the existing inner anchors as the
	 * keyboard-accessible nav target.
	 */
	const handleCardClick = ( root, e ) => {
		const card = e.target.closest( '.wl-card[data-product-permalink]' );
		if ( ! card || ! root.contains( card ) ) {
			return;
		}
		// Let dedicated controls (remove, add-to-cart, inner links) handle
		// their own click. Anything else on the card surface navigates.
		if (
			e.target.closest(
				'[data-wishlist-remove], [data-add-to-cart], a, button'
			)
		) {
			return;
		}
		const permalink = card.getAttribute( 'data-product-permalink' );
		if ( permalink ) {
			window.location.href = permalink;
		}
	};

	const init = () => {
		const root = document.querySelector( '[data-wishlist-page]' );
		if ( ! root ) {
			return;
		}

		const render = async () => {
			const ids = loadIds();

			if ( ! ids.length ) {
				showEmpty( root );
				return;
			}

			const products = await fetchProducts( ids );

			// fetchProducts returns null on network/HTTP failure. Don't
			// self-heal localStorage from that — keep the user's saved
			// IDs intact and show the empty state with a hint so they
			// know something went wrong rather than silently losing data.
			if ( products === null ) {
				showEmpty( root, /* hadError */ true );
				return;
			}

			// Self-heal: drop IDs that no longer resolve to a published
			// product (Store API responded successfully but didn't include
			// them — they were deleted or hidden from catalog).
			const returnedIds = new Set( products.map( p => p.id ) );
			const cleaned = ids.filter( id => returnedIds.has( id ) );
			if ( cleaned.length !== ids.length ) {
				try {
					localStorage.setItem( WL_KEY, JSON.stringify( cleaned ) );
				} catch ( err ) {}
				// eslint-disable-next-line no-console
				console.warn(
					'[wishlist] Dropping unresolved product IDs from localStorage:',
					ids.filter( id => ! returnedIds.has( id ) )
				);
			}

			if ( ! products.length ) {
				showEmpty( root );
				return;
			}

			renderGrid( root, products );
		};

		root.addEventListener( 'click', e => {
			handleRemoveClick( root, e );
			handleAddToCartClick( root, e );
			handleCardClick( root, e );
		} );

		document.addEventListener( 'icf:wishlist-changed', () => {
			render();
		} );

		window.addEventListener( 'storage', e => {
			if ( e.key === WL_KEY ) {
				render();
			}
		} );

		render();
	};

	onReady( init );
} )();
